import { useState } from 'react';
import { Plus, Edit, Trash2, Eye, Search, MapPin, Building2, ToggleLeft, ToggleRight } from 'lucide-react';
import { Property, nearbyPlaces } from '../../data/mockData';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Link } from 'react-router';
import { useProperties } from '../../contexts/PropertyContext';
import { toast } from 'sonner';

export default function AdminProperties() {
  const { properties, deleteProperty, togglePropertyActive, loading } = useProperties();
  const [searchTerm, setSearchTerm] = useState('');

  const filteredProperties = properties.filter((property: Property) =>
    property.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    property.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = async (id: string) => {
    if (confirm('هل أنت متأكد من حذف هذا العقار؟')) {
      try {
        await deleteProperty(id);
        toast.success('تم حذف العقار بنجاح!');
      } catch (error) {
        console.error('Error deleting property:', error);
        toast.error('حدث خطأ أثناء حذف العقار. الرجاء المحاولة مرة أخرى.');
      }
    }
  };

  const handleToggleActive = async (id: string) => {
    try {
      await togglePropertyActive(id);
      const property = properties.find(p => p.id === id);
      const status = property?.active ? 'sold' : 'active';
      toast.success(`Property marked as ${status}!`);
    } catch (error) {
      console.error('Error toggling property status:', error);
      toast.error('Error updating property status. Please try again.');
    }
  };

  return (
    <div>
      {/* Debug Info - Remove in production */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-bold text-blue-800 mb-2">Debug Info:</h3>
        <p className="text-sm text-blue-600">Total Properties: {properties.length}</p>
        <p className="text-sm text-blue-600">Loading: {loading ? 'Yes' : 'No'}</p>
        {properties[0] && (
          <p className="text-sm text-blue-600">
            First Property Main Image: {properties[0].mainImage ? 'Has Image' : 'No Image'}
          </p>
        )}
        {properties[0] && (
          <p className="text-sm text-blue-600">
            First Property Gallery: {properties[0].galleryImages?.length || 0} images
          </p>
        )}
      </div>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">إدارة العقارات</h1>
          <p className="text-gray-600">إضافة وتعديل وحذف العقارات</p>
        </div>

        <Link to="/admin/properties/add">
          <Button className="bg-[#D4AF37] hover:bg-[#B8960C] text-white" disabled={loading}>
            <Plus className="w-5 h-5 ml-2" />
            إضافة عقار جديد
          </Button>
        </Link>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="relative">
          <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            type="text"
            placeholder="البحث عن عقار..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pr-10"
          />
        </div>
      </div>

      {/* Properties Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-right py-4 px-6 text-sm font-medium text-gray-600">Property</th>
                <th className="text-right py-4 px-6 text-sm font-medium text-gray-600">Type</th>
                <th className="text-right py-4 px-6 text-sm font-medium text-gray-600">Category</th>
                <th className="text-right py-4 px-6 text-sm font-medium text-gray-600">Price</th>
                <th className="text-right py-4 px-6 text-sm font-medium text-gray-600">Area</th>
                <th className="text-right py-4 px-6 text-sm font-medium text-gray-600">Floor</th>
                <th className="text-right py-4 px-6 text-sm font-medium text-gray-600">Nearby Places</th>
                <th className="text-right py-4 px-6 text-sm font-medium text-gray-600">Status</th>
                <th className="text-right py-4 px-6 text-sm font-medium text-gray-600">Toggle</th>
                <th className="text-right py-4 px-6 text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProperties.map((property, index) => (
                <tr key={property.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="py-4 px-6">
                    <div className="font-medium text-gray-800">{property.title}</div>
                    <div className="text-sm text-gray-600">{property.location}</div>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                      property.type === 'rent' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {property.type === 'rent' ? 'للإيجار' : 'للبيع'}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-gray-700">{property.category}</td>
                  <td className="py-4 px-6 font-medium text-gray-800">
                    {property.price.toLocaleString('ar-SA')} ر.س
                  </td>
                  <td className="py-4 px-6 text-gray-700">{property.area} م²</td>
                  <td className="py-4 px-6 text-gray-700">
                    <div className="flex items-center gap-1">
                      <Building2 className="w-4 h-4 text-gray-400" />
                      <span>{property.floor || 1}</span>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-1 flex-wrap">
                      {property.nearby && property.nearby.length > 0 ? (
                        <>
                          <MapPin className="w-4 h-4 text-[#D4AF37]" />
                          <span className="text-xs text-gray-600">
                            {property.nearby.slice(0, 2).map(id => 
                              nearbyPlaces.find(p => p.id === id)?.name
                            ).filter(Boolean).join('، ')}
                            {property.nearby.length > 2 && ` +${property.nearby.length - 2}`}
                          </span>
                        </>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    {property.featured && (
                      <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-[#FFF9E6] text-[#8B7109] mb-1 block">
                        MUMAYYAZ
                      </span>
                    )}
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleActive(property.id)}
                        className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          property.active 
                            ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                        }`}
                      >
                        {property.active ? (
                          <>
                            <ToggleRight className="w-4 h-4" />
                            Active
                          </>
                        ) : (
                          <>
                            <ToggleLeft className="w-4 h-4" />
                            Sold
                          </>
                        )}
                      </button>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-2">
                      <Link to={`/property/${property.id}`} target="_blank">
                        <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-blue-600">
                          <Eye className="w-5 h-5" />
                        </button>
                      </Link>
                      <Link to={`/admin/properties/${property.id}/edit`}>
                        <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600">
                          <Edit className="w-5 h-5" />
                        </button>
                      </Link>
                      <button 
                        onClick={() => handleDelete(property.id)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-red-600"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
